import { useEffect, useRef, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface OverlayProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly className?: string;
  /** Show visible backdrop (bg-black/40). When false, backdrop is transparent (click-outside only). */
  readonly backdrop?: boolean;
  /** Center children in viewport. Default true. */
  readonly center?: boolean;
}

function useFocusTrap(ref: React.RefObject<HTMLDivElement | null>, open: boolean) {
  const handleTab = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !ref.current) return;

      const focusable = ref.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [ref],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [open, handleTab]);
}

export function Overlay({
  open,
  onClose,
  children,
  className,
  backdrop = true,
  center = true,
}: OverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useFocusTrap(containerRef, open);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 ${backdrop ? "bg-black/40 animate-fade-in" : ""}`}
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        aria-hidden="true"
      />
      {/* Content */}
      <div
        ref={containerRef}
        data-ph-no-capture
        className={`fixed z-50 ${center ? "inset-0 flex items-center justify-center p-4 pointer-events-none" : ""} ${className ?? ""}`}
      >
        {center ? (
          <div
            className="pointer-events-auto animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </>,
    document.body,
  );
}
